import { useState, useCallback, useEffect } from "react";

export const usePageNavigation = (totalBricks: number, bricksPerPage: number = 3) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);

  const totalPages = Math.ceil(totalBricks / bricksPerPage);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePreviousPage();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNextPage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlePreviousPage, handleNextPage]);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(e.touches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          handleNextPage();
        } else {
          handlePreviousPage();
        }
      }
    },
    [touchStartX, handleNextPage, handlePreviousPage]
  );

  // Reset to first page when total bricks changes
  useEffect(() => {
    setCurrentPage(0);
  }, [totalBricks]);

  return {
    currentPage,
    totalPages,
    setCurrentPage,
    handlePreviousPage,
    handleNextPage,
    handleTouchStart,
    handleTouchEnd,
  };
};
